import { Directive, ElementRef, HostListener, OnDestroy, OnInit, Optional, Self } from "@angular/core";
import { NgControl } from "@angular/forms";
import { Subscription } from "rxjs";
import {
	formatCurrencyDisplayFromCents,
	normalizeSalaryValue,
	numericToCents,
	parseDigitsToCents,
} from "@core/utils/currency-value.helper";

@Directive({
	selector: "input[appCurrencyCentsInput]",
})
export class CurrencyCentsInputDirective implements OnInit, OnDestroy {
	private isUpdating = false;
	private valueChangesSub?: Subscription;

	constructor(
		private readonly elementRef: ElementRef<HTMLInputElement>,
		@Optional() @Self() private readonly ngControl: NgControl
	) {}

	ngOnInit(): void {
		this.syncDisplayFromControl(this.ngControl?.control?.value);

		const control = this.ngControl?.control;
		if (!control) {
			return;
		}

		this.valueChangesSub = control.valueChanges.subscribe(value => {
			if (this.isUpdating) {
				return;
			}

			this.syncDisplayFromControl(value);
		});
	}

	ngOnDestroy(): void {
		this.valueChangesSub?.unsubscribe();
	}

	@HostListener("input")
	onInput(): void {
		if (this.isUpdating || this.elementRef.nativeElement.disabled) {
			return;
		}

		this.applyDigits(this.elementRef.nativeElement.value.replace(/\D/g, ""));
	}

	@HostListener("blur")
	onBlur(): void {
		if (this.elementRef.nativeElement.disabled) {
			return;
		}

		const control = this.ngControl?.control;
		if (!control) {
			return;
		}

		this.syncDisplayFromControl(control.value);
	}

	private applyDigits(digits: string): void {
		const input = this.elementRef.nativeElement;

		if (!digits) {
			this.updateControl(null, "");
			return;
		}

		const cents = parseDigitsToCents(digits);
		const numeric = cents / 100;
		const display = `R$ ${formatCurrencyDisplayFromCents(cents)}`;

		this.updateControl(numeric, display);
		input.setSelectionRange(display.length, display.length);
	}

	private syncDisplayFromControl(value: unknown): void {
		const normalized = normalizeSalaryValue(value);
		const display = normalized === null ? "" : `R$ ${formatCurrencyDisplayFromCents(numericToCents(normalized))}`;

		if (this.elementRef.nativeElement.value !== display) {
			this.elementRef.nativeElement.value = display;
		}
	}

	private updateControl(numeric: number | null, display: string): void {
		this.isUpdating = true;
		this.ngControl?.control?.setValue(numeric, { emitEvent: true });
		this.elementRef.nativeElement.value = display;
		this.isUpdating = false;
	}
}
